import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Users, Languages, CalendarCheck, FileSignature, Plane } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionHeading } from "../components/site/SectionHeading";
import { Reveal } from "../components/site/Reveal";
import { ProcessTabs } from "../components/site/ProcessTabs";
import { FaqList } from "../components/site/FaqList";
import { CtaBand } from "../components/site/CtaBand";
import { FAQ_EMPLOYER } from "../content/site";
import { useSeo } from "../lib/useSeo";

const SERVICES = [
  { icon: ClipboardList, t: "Personalbedarf gemeinsam klären", d: "Wir besprechen Tätigkeit, Anforderungen, Arbeitszeiten, Vergütung, Einsatzort und Ihren zeitlichen Rahmen – und halten alles in einem Anforderungsprofil fest." },
  { icon: Users, t: "Passende Kandidaten auswählen", d: "Aus unserem Bewerberpool wählen wir Personen aus, deren Profil zu Ihren Anforderungen passt." },
  { icon: Languages, t: "Qualifikation, Erfahrung, Sprache, Verfügbarkeit", d: "Wir berücksichtigen Berufsqualifikationen, praktische Erfahrung, Sprachkenntnisse und den realistischen Verfügbarkeitszeitpunkt." },
  { icon: CalendarCheck, t: "Profile vorstellen, Gespräche koordinieren", d: "Sie erhalten aussagekräftige Bewerberprofile. Wir organisieren Vorstellungsgespräche – häufig zunächst per Video." },
  { icon: FileSignature, t: "Konditionen schriftlich vereinbaren", d: "Vor Beginn der Zusammenarbeit vereinbaren wir Leistungen, Vermittlungsprovision und Zahlungsbedingungen schriftlich." },
  { icon: Plane, t: "Einstellung und Arbeitsaufnahme begleiten", d: "Wir begleiten die organisatorischen Schritte rund um Unterlagen, Visumverfahren, ggf. Anerkennung und die Abstimmung des Arbeitsbeginns." },
];

export default function Companies() {
  useSeo("Für Unternehmen", "Internationale Direktvermittlung für Unternehmen in ganz Deutschland: Bedarfsanalyse, Kandidatenauswahl, Vorstellungsgespräche und Begleitung bis zum Arbeitsbeginn.", "/unternehmen");
  return (
    <>
      <PageHero testid="companies-hero" eyebrow="Für Unternehmen" lines={["Personal aus dem Ausland –", "direkt bei Ihnen angestellt."]}
        intro="Sie suchen Mitarbeiterinnen und Mitarbeiter, die in Ihrem Unternehmen langfristig ankommen? Wir klären Ihren Bedarf, stellen geeignete Kandidaten aus unserem Bewerberpool vor und begleiten die Vermittlung bis zum vereinbarten Arbeitsbeginn."
        image="/img/industry.webp" imageAlt="Symbolbild: Schweißer in einer Produktionshalle">
        <Link to="/personal-anfragen" className="group inline-flex items-center gap-3 rounded-full bg-[color:var(--navy)] px-7 py-4 font-semibold text-white hover:bg-[color:var(--navy-deep)]" data-testid="companies-hero-cta">Personal anfragen <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></Link>
      </PageHero>

      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="companies-services">
        <div className="container-x">
          <SectionHeading eyebrow="Leistungen" title="Was wir für Sie übernehmen" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.t} delay={(i % 3) * 0.06}>
                <article className="group h-full rounded-3xl bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-30px_rgba(27,47,91,0.5)]" data-testid={`service-card-${i}`}>
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[color:var(--navy)] text-white transition-colors group-hover:bg-[color:var(--teal)]"><s.icon size={22} /></span>
                  <h3 className="mt-6 text-lg font-bold text-[color:var(--navy)]">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 max-w-3xl text-sm leading-relaxed text-slate-600">
              Wichtig: Wir versprechen keine pauschal sofort verfügbaren Mitarbeiter. Ob und wann passende Personen eingestellt werden können, hängt von Beruf, Anforderungen, Bewerberverfügbarkeit und den behördlichen Verfahren ab. Über Visa und Aufenthaltstitel entscheiden ausschließlich die zuständigen Behörden.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="companies-fees">
        <div className="container-x grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5"><SectionHeading eyebrow="Vergütung" title="Transparent und individuell vereinbart." /></div>
          <Reveal delay={0.1} className="lg:col-span-7">
            <blockquote className="relative rounded-[2rem] border border-slate-200 p-8 md:p-12">
              <span className="font-serif-accent absolute -top-8 left-8 text-8xl leading-none text-[color:var(--teal)]" aria-hidden="true">“</span>
              <p className="text-xl md:text-2xl font-semibold leading-snug text-[color:var(--navy)]">
                Die Vermittlungsprovision wird vor Beginn der Zusammenarbeit individuell vereinbart. Sie erhalten ein transparentes Angebot mit klar beschriebenen Leistungen und Zahlungsbedingungen.
              </p>
              <p className="mt-6 text-sm text-slate-600">Das Gehalt der vermittelten Arbeitskraft zahlen Sie als Arbeitgeber direkt. Feste Preise veröffentlichen wir nicht, da Aufwand und Leistungsumfang je nach Beruf und Einzelfall unterschiedlich sind.</p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="bg-[color:var(--mist)] py-24 md:py-32" data-testid="companies-process">
        <div className="container-x">
          <SectionHeading eyebrow="Ablauf für Arbeitgeber" title="Von der Anfrage bis zum ersten Arbeitstag" className="mb-12" />
          <ProcessTabs initial="employer" />
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="companies-faq">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow="Fragen von Unternehmen" title="Häufige Fragen" /></div>
          <div className="lg:col-span-8"><FaqList items={FAQ_EMPLOYER} prefix="companies-faq" /></div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
